// components/CreateLugarModal.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { FaTimes, FaImage, FaMapMarkerAlt } from 'react-icons/fa';
import LocationSelector from '../LocationSelector';

const CreateLugarModal = ({ newLugar, setNewLugar, handleCreateSubmit, isLoading, onClose }) => {
  const [previewImage, setPreviewImage] = useState('');
  const [imageError, setImageError] = useState(false);

  // Actualizar la vista previa cuando cambia la URL de la imagen
  useEffect(() => {
    if (newLugar.imagen) {
      setPreviewImage(newLugar.imagen);
    } else {
      setPreviewImage('');
    }
  }, [newLugar.imagen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewLugar(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // ✅ Usar useCallback para evitar re-renders innecesarios
  const handleRegionChange = useCallback((regionName) => {
    setNewLugar(prev => ({
      ...prev,
      region: regionName, // ✅ Ahora guardamos el nombre de la región
      ciudad: '' // Limpiar ciudad cuando cambia la región
    }));
  }, [setNewLugar]);

  const handleCommuneChange = useCallback((communeName) => {
    setNewLugar(prev => ({
      ...prev,
      ciudad: communeName
    }));
  }, [setNewLugar]);

  const testImageUrl = () => {
    if (newLugar.imagen) {
      setImageError(false);
      
      const img = new Image();
      img.onload = () => {
        setPreviewImage(newLugar.imagen);
        setImageError(false);
      };
      img.onerror = () => {
        setImageError(true);
      };
      img.src = newLugar.imagen;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 overflow-y-auto backdrop-blur-sm">
      <div className="bg-zinc-800 rounded-xl shadow-2xl max-w-4xl w-full p-8 my-8 max-h-[90vh] overflow-y-auto border border-zinc-700">
        <div className="flex justify-between items-center mb-8">
          <h3 className="text-2xl font-bold text-white">Nuevo Lugar de Evento</h3>
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-white hover:bg-zinc-700 p-2 rounded-full transition-all duration-200"
            aria-label="Cerrar"
          >
            <FaTimes />
          </button>
        </div>
        
        <form onSubmit={(e) => handleCreateSubmit(e, newLugar)} className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300">
                Nombre del Lugar *
              </label>
              <input
                type="text"
                name="nombre"
                value={newLugar.nombre}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-zinc-700/50 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                required
                placeholder="Nombre del lugar"
              />
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300">
                URL de Imagen *
              </label>
              <div className="flex gap-4">
                <div className="flex-grow">
                  <div className="flex gap-2">
                    <input
                      type="url"
                      name="imagen"
                      value={newLugar.imagen}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 bg-zinc-700/50 border ${imageError ? 'border-red-500' : 'border-zinc-600'} rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200`}
                      required
                      placeholder="https://ejemplo.com/imagen.jpg"
                    />
                    <button
                      type="button"
                      onClick={testImageUrl}
                      className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm"
                    >
                      Probar
                    </button>
                  </div>
                  {imageError && (
                    <p className="text-red-400 text-xs mt-1">
                      No se pudo cargar la imagen. Verifica la URL.
                    </p>
                  )}
                </div>
                
                {/* Vista previa de la imagen */}
                <div className="w-24 h-24 bg-zinc-700/30 border border-zinc-600 rounded-lg overflow-hidden flex items-center justify-center">
                  {previewImage && !imageError ? (
                    <img 
                      src={previewImage} 
                      alt="Vista previa" 
                      className="w-full h-full object-cover"
                      onError={() => setImageError(true)}
                    />
                  ) : (
                    <FaImage className="text-zinc-500 text-3xl" />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">
              Dirección *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaMapMarkerAlt className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                name="direccion"
                value={newLugar.direccion}
                onChange={handleChange}
                className="pl-10 w-full px-4 py-3 bg-zinc-700/50 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                required
                placeholder="Dirección completa del lugar"
              />
            </div>
          </div>

          {/* Selector de Ubicación */}
          <div className="space-y-2">
            <LocationSelector
              selectedRegion={newLugar.region}
              selectedCommune={newLugar.ciudad}
              onRegionChange={handleRegionChange}
              onCommuneChange={handleCommuneChange}
              disabled={isLoading}
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">
              Descripción *
            </label>
            <textarea
              name="descripcion"
              value={newLugar.descripcion}
              onChange={handleChange}
              rows="4"
              className="w-full px-4 py-3 bg-zinc-700/50 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 resize-none"
              required
              placeholder="Descripción del lugar, capacidad, características especiales..."
            ></textarea>
          </div>

          {/* Resumen de ubicación */}
          {newLugar.region && newLugar.ciudad && newLugar.direccion &&(
            <div className="bg-zinc-700/30 border border-zinc-600 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-300 mb-2">Ubicación seleccionada:</h4>
              <p className="text-white">
                <FaMapMarkerAlt className="inline mr-2 text-red-500" />
                {newLugar.direccion}, {newLugar.ciudad}, {newLugar.region} {/* ✅ Ahora mostramos directamente el nombre */}
              </p>
            </div>
          )}
          
          <div className="flex justify-end space-x-4 pt-4 border-t border-zinc-700">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-zinc-700 text-white rounded-lg hover:bg-zinc-600 transition-all duration-200"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading || imageError || !newLugar.region || !newLugar.ciudad}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center min-w-[140px] transition-all duration-200"
              title={
                imageError ? 'Corrige la URL de la imagen para continuar' : 
                !newLugar.region || !newLugar.ciudad ? 'Selecciona región y comuna para continuar' : ''
              }
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creando...
                </>
              ) : (
                'Crear Lugar'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateLugarModal;